import { cn } from '@/lib/utils'
import React from 'react'

type UseTextTruncationResult = {
  textRef: React.RefObject<HTMLElement>
  isTruncated: boolean
}
function useTextTruncation(): UseTextTruncationResult {
  const [isTruncated, setIsTruncated] = React.useState(false)
  const textRef = React.useRef<HTMLElement>(null)

  const checkTruncation = React.useCallback(() => {
    if (textRef.current) {
      const element = textRef.current
      // Check if text is truncated by comparing scroll width to client width
      // Add a small buffer (1px) to account for rounding differences
      const isTextTruncated = element.scrollWidth > element.clientWidth + 1
      setIsTruncated(isTextTruncated)
    }
  }, [])

  React.useEffect(() => {
    // Initial check
    checkTruncation()

    // Use ResizeObserver for accurate size change detection
    let resizeObserver: ResizeObserver | null = null

    if (textRef.current && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        // Debounce resize observer events to avoid excessive calls
        // Use requestAnimationFrame for better performance than setTimeout
        requestAnimationFrame(() => {
          checkTruncation()
        })
      })
      resizeObserver.observe(textRef.current)
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
    }
  }, [checkTruncation])

  return { textRef, isTruncated }
}

const RenderTextTruncated = ({
  render,
  text,
  className,
}: Readonly<{
  render: ({
    isTruncated,
    children,
  }: {
    isTruncated: boolean
    children: React.ReactNode
  }) => React.ReactNode
  text: string
  className?: string
}>) => {
  const { isTruncated, textRef } = useTextTruncation()
  return render({
    isTruncated,
    children: (
      <span ref={textRef} className={cn('truncate', className)}>
        {text}
      </span>
    ),
  })
}

export { RenderTextTruncated, useTextTruncation }
